import { memo, useMemo } from 'react'
import { BroadcastTypeEnum, ChatTypeEnum } from '@medentee/enums'

import {
  createLoadingSelector,
  useAppSelector,
  GET_CHAT_ROOMS,
  GET_BROADCASTS,
  isBusinessAccountSelector,
  selectHighlightedChatIdsByType
} from 'store'
import { TUnityHubRoomsNavLayoutItem, UnityHubRoomsNavLayout } from 'App/components/UnityHub'
import { UnityHubRoomsBroadcastList } from 'App/components/UnityHub/UnityHubRoomsBroadcastList'
import { UnityHubRoomsNavCommunitiesList } from 'App/components/UnityHub/UnityHubRoomsNavCommunitiesList'

import { EventsRoomsNavTextList } from './EventsRoomsNavTextList'
import { EventsRoomsNavHighlights } from './EventsRoomsNavHighlights'

const loadingChatRoomsSelector = createLoadingSelector([GET_CHAT_ROOMS])

type TEventsRoomsNavProps = {
  eventId: string
}

const EventsRoomsNavView = ({ eventId }: TEventsRoomsNavProps) => {
  const loadingChatRooms = useAppSelector(
    (state) => loadingChatRoomsSelector(state) && !state.chat.chatRooms.ids.length
  )
  const loadingBroadcasts = useAppSelector(createLoadingSelector([GET_BROADCASTS]))
  const broadcastsIdsLength = useAppSelector((state) => !!state.broadcasts.data.ids.length)
  const newsIds = useAppSelector(selectHighlightedChatIdsByType(ChatTypeEnum.EVENT_NEWS))
  const textChatIds = useAppSelector(selectHighlightedChatIdsByType(ChatTypeEnum.EVENT_CHANNEL))
  const isBusinessAccount = useAppSelector(isBusinessAccountSelector)

  const items = useMemo<TUnityHubRoomsNavLayoutItem[]>(
    () => [
      {
        key: 'highlights',
        item: <EventsRoomsNavHighlights />,
        loading: loadingChatRooms
      },
      {
        key: 'broadcast',
        item: <UnityHubRoomsBroadcastList entityId={eventId} type={BroadcastTypeEnum.EVENT} />,
        loading: loadingBroadcasts,
        hidden: !isBusinessAccount && !broadcastsIdsLength
      },

      {
        key: 'communities',
        item: <UnityHubRoomsNavCommunitiesList eventId={eventId} />
      },
      {
        key: 'news-channel',
        item: <EventsRoomsNavTextList variant="news" eventId={eventId} />,
        loading: loadingChatRooms,
        hidden: !isBusinessAccount && !newsIds.length
      },
      {
        key: 'text-channel',
        item: <EventsRoomsNavTextList variant="chat" eventId={eventId} />,
        loading: loadingChatRooms,
        hidden: !isBusinessAccount && !textChatIds.length
      }
    ],
    [
      eventId,
      loadingBroadcasts,
      isBusinessAccount,
      broadcastsIdsLength,
      loadingChatRooms,
      newsIds.length,
      textChatIds.length
    ]
  )

  return <UnityHubRoomsNavLayout items={items} />
}

export const EventsRoomsNav = memo(EventsRoomsNavView)
